import { Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { RiskBannerWeb } from '@/components/common/RiskBannerWeb';
import type {
  EvmCallRisk,
  EvmInnerCall,
  EvmMetadataIssue,
  SignedEvmCallAnalysis,
} from '@/lib/evmCallSafety';
import { formatBalance } from '@/types/vault';

const { Text, Paragraph } = Typography;

const RISK_TAG_COLOR: Record<EvmCallRisk, string> = {
  none: 'blue',
  warning: 'orange',
  danger: 'red',
};

const ARG_ORDER = [
  'to',
  'from',
  'spender',
  'operator',
  'owner',
  'amount',
  'approved',
  'deadline',
] as const;

const hexBoxStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  wordBreak: 'break-all',
  background: 'var(--color-bg-secondary, rgba(0,0,0,0.04))',
  padding: '6px 10px',
  borderRadius: 6,
  maxHeight: 160,
  overflow: 'auto',
  margin: '4px 0 0',
};

function EvmCallRow({ call, chain }: { call: EvmInnerCall; chain: string }) {
  const { t } = useTranslation();
  const args = call.args ?? {};
  const argKeys = ARG_ORDER.filter((k) => args[k] !== undefined);
  return (
    <div
      className="surface-box mt-4"
      data-testid="evm-signed-call"
      data-kind={call.kind}
      data-blocked={call.blocked ? 'true' : 'false'}
    >
      <div className="hstack-wrap gap-8">
        <Text type="secondary" className="fs-12">
          {t('vaults.signedCalls.call', { index: call.index + 1 })}
        </Text>
        <Tag color={RISK_TAG_COLOR[call.risk]} style={{ lineHeight: '18px' }}>
          {t(`vaults.signedCalls.kinds.${call.kind}`)}
        </Tag>
        {call.blocked && (
          <Tag color="red" style={{ lineHeight: '18px' }} data-testid="evm-signed-call-blocked">
            {t('vaults.signedCalls.blocked')}
          </Tag>
        )}
        {call.unlimited && (
          <Tag color="red" style={{ lineHeight: '18px' }}>
            {t('vaults.signedCalls.unlimited')}
          </Tag>
        )}
      </div>
      <div className="vstack gap-2 mt-4">
        <div>
          <Text type="secondary" className="fs-12">
            {t('vaults.signedCalls.target')}:{' '}
          </Text>
          <Text copyable={{ text: call.target }} className="mono fs-12 break-all">
            {call.target}
          </Text>
        </div>
        {call.value !== '0' && (
          <div>
            <Text type="secondary" className="fs-12">
              {t('vaults.signedCalls.value')}:{' '}
            </Text>
            <Text className="mono fs-12">
              {formatBalance(call.value, chain)} ({call.value} wei)
            </Text>
          </div>
        )}
        {(call.method || call.selector) && (
          <div>
            <Text type="secondary" className="fs-12">
              {t('vaults.signedCalls.method')}:{' '}
            </Text>
            <Text className="mono fs-12">{call.method ?? call.selector}</Text>
          </div>
        )}
        {argKeys.map((key) => (
          <div key={key}>
            <Text type="secondary" className="fs-12">
              {t(`vaults.signedCalls.args.${key}`)}:{' '}
            </Text>
            <Text copyable={{ text: args[key] ?? '' }} className="mono fs-12 break-all">
              {args[key]}
            </Text>
          </div>
        ))}
        {call.data !== '0x' && !call.method && (
          <div>
            <Text type="secondary" className="fs-12">
              {t('vaults.signedCalls.rawData')}:
            </Text>
            <div style={hexBoxStyle}>
              <Paragraph copyable className="mono fs-12 m-0">
                {call.data}
              </Paragraph>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Every inner call of the signed UserOperation, in order. */
export function EvmCallList({ calls, chain }: { calls: EvmInnerCall[]; chain: string }) {
  return (
    <div data-testid="evm-signed-calls">
      {calls.map((call) => (
        <EvmCallRow key={call.index} call={call} chain={chain} />
      ))}
    </div>
  );
}

interface EvmSignedCallReviewProps {
  chain: string;
  /** null when the proposal carries no UserOperation at all. */
  analysis: SignedEvmCallAnalysis | null;
  issues: EvmMetadataIssue[];
  /** A plain ERC-20 transfer that matches the metadata needs no contract banner. */
  isExpectedTokenTransfer: boolean;
}

/**
 * Banners derived from the SIGNED UserOperation (ENTX-A-1). Critical banners
 * here correspond to the page disabling sign / approve; the contract-call
 * banner is advisory but always shown when calldata is present.
 */
export function EvmSignedCallReview({
  chain,
  analysis,
  issues,
  isExpectedTokenTransfer,
}: EvmSignedCallReviewProps) {
  const { t } = useTranslation();

  if (!analysis) {
    return (
      <RiskBannerWeb
        severity="critical"
        title={t('vaults.signedCalls.missingTitle')}
        messages={[t('vaults.signedCalls.missingDesc')]}
        className="proposal-page-banner"
      />
    );
  }
  if (!analysis.ok) {
    return (
      <RiskBannerWeb
        severity="critical"
        title={t('vaults.signedCalls.undecodableTitle')}
        messages={[t('vaults.signedCalls.undecodableDesc', { code: analysis.error })]}
        className="proposal-page-banner"
      />
    );
  }

  return (
    <>
      {analysis.blocked && (
        <RiskBannerWeb
          severity="critical"
          title={t('vaults.signedCalls.blockedTitle')}
          messages={[t('vaults.signedCalls.blockedDesc')]}
          className="proposal-page-banner"
        >
          <EvmCallList calls={analysis.calls.filter((c) => c.blocked)} chain={chain} />
        </RiskBannerWeb>
      )}
      {issues.length > 0 && (
        <RiskBannerWeb
          severity="critical"
          title={t('vaults.signedCalls.mismatchTitle')}
          messages={[
            t('vaults.signedCalls.mismatchDesc'),
            ...issues.map((issue) => t(`vaults.signedCalls.issues.${issue}`)),
          ]}
          className="proposal-page-banner"
        />
      )}
      {!analysis.blocked && analysis.hasContractInteraction && !isExpectedTokenTransfer && (
        <RiskBannerWeb
          severity="high"
          title={t('vaults.signedCalls.contractCallTitle')}
          messages={[t('vaults.signedCalls.contractCallDesc')]}
          className="proposal-page-banner"
        >
          <EvmCallList calls={analysis.calls} chain={chain} />
        </RiskBannerWeb>
      )}
    </>
  );
}
